import { useUser } from '@folklore/auth';
import classNames from 'classnames';
import { lazy, Suspense, useState } from 'react';

import { useDocumentShareUrlGenerator } from '../../hooks/useUrlGenerator';

import { useHasPermission, useHasJoinedGroup } from '../../contexts/AuthContext';
import ToggleButton from '../buttons/ToggleButton';
import CopyField from '../fields/CopyField';
import ShareUrlField from '../fields/ShareUrlField';
import TextField from '../fields/TextField';
import CardTitle from '../typography/CardTitle';
import Dialog from './Dialog';
import Modal from './Modal';

import styles from '../../styles/popups/share-modal.module.css';

const DocumentShareImages = lazy(() => import('../partials/DocumentShareImages'));

interface ShareModalProps {
    document: FullDocument | null;
    className?: string | null;
    onClosed?: (() => void) | null;
}

function ShareModal({ document, className = null, onClosed = null }: ShareModalProps) {
    const [opened, setOpened] = useState(true);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const { email } = useUser();
    const isPro = useHasJoinedGroup('pro');
    const hasAttributionAccess = useHasPermission('attribution');
    const shareUrl = useDocumentShareUrlGenerator();
    const [addAttribution, setAddAttribution] = useState(isPro);
    const onChangeAttribution = () => {
        setAddAttribution((v) => !v);
    };

    const onRequestClose = () => {
        setIsFullscreen(false);
        setOpened(false);
    };

    const onEditingChange = (isEditing: boolean) => {
        setIsFullscreen(isEditing);
    };

    const url = shareUrl(document, { attribution: addAttribution });

    const fields = [
        {
            id: 'url',
            label: 'Lien',
            value: url,
        },
    ].filter(({ value }) => value !== null);

    return (
        <Modal
            id="share"
            opened={opened}
            fullscreen={isFullscreen}
            withBackdrop
            withTransition
            // withScrollLock
            requestClose={onRequestClose}
            className={classNames([
                styles.container,
                {
                    [styles.fullscreen]: isFullscreen,
                },
                className,
            ])}
            innerClassName={styles.inner}
            onClosed={onClosed}
        >
            <Dialog
                fullscreen={isFullscreen}
                closeable={!isFullscreen}
                onClose={onRequestClose}
                className={styles.dialog}
            >
                <div className={styles.section}>
                    <CardTitle className={styles.title}>Partage</CardTitle>
                    {fields.map(({ id, label, value }) => (
                        <div className={styles.metadata} key={`metadata-${id}`}>
                            <label className={styles.label} htmlFor={`metadata-${id}`}>
                                {label} :
                            </label>
                            {id === 'url' ? (
                                <ShareUrlField value={value} className={styles.field} />
                            ) : (
                                <CopyField value={value} className={styles.field}>
                                    <TextField
                                        id={`metadata-${id}`}
                                        type="text"
                                        value={value}
                                        readOnly
                                        selectOnFocus
                                        className={styles.input}
                                    />
                                </CopyField>
                            )}
                        </div>
                    ))}
                    {hasAttributionAccess ? (
                        <div className={styles.settings}>
                            <label className={styles.label}>Ajouter le suivi personnalisé :</label>
                            <ToggleButton
                                value={addAttribution}
                                onChange={onChangeAttribution}
                                className={styles.toggle}
                            />
                            <div className={styles.user}>{email}</div>
                        </div>
                    ) : null}
                </div>
                {hasAttributionAccess ? (
                    <div className={styles.section}>
                        <CardTitle className={styles.title}>Images</CardTitle>
                        <Suspense fallback={null}>
                            <DocumentShareImages
                                document={document}
                                onEditingChange={onEditingChange}
                                className={styles.images}
                            />
                        </Suspense>
                    </div>
                ) : null}
            </Dialog>
        </Modal>
    );
}

export default ShareModal;
